<template>
  <div class="office-supplies-table">
    <div v-if="rowLayout">
      <el-table size="medium" :header-cell-style="{ background: '#f5f7fa', padding: '3px 0' }" :border="true" :data="_value"
        style="width: 100%" :summary-method="getSummaries" show-summary>
        <el-table-column fixed type="index" label="序号" width="55"></el-table-column>
        
        <!-- 物品名称列 - 添加必填标识 -->
        <el-table-column prop="goodsName" label="物品名称" min-width="160">
          <template slot="header">
            <span class="required-field">物品名称</span>
          </template>
          <template slot-scope="scope">
            <div class="input-container">
              <el-input v-if="!readerMode" size="small" v-model="_value[scope.$index].goodsName" placeholder="请输入"
                :class="{ 'error-border': hasError(scope.$index, 'goodsName') }" 
                maxlength="24" show-word-limit 
                @blur="validateField(scope.$index, 'goodsName', _value[scope.$index].goodsName)" />
              <span v-else>{{ _value[scope.$index].goodsName || '-' }}</span>
            </div>
            <div v-if="hasError(scope.$index, 'goodsName')" class="error-message">
              {{ getErrorMessage(scope.$index, 'goodsName') }}
            </div>
          </template>
        </el-table-column>
        
        <el-table-column prop="specification" label="规格" min-width="140">
          <template slot-scope="scope">
            <div class="input-container">
              <el-input v-if="!readerMode" size="small" v-model="_value[scope.$index].specification" placeholder="请输入"
                maxlength="24" show-word-limit />
              <span v-else>{{ _value[scope.$index].specification || '-' }}</span>
            </div>
          </template>
        </el-table-column>
        
        <!-- 单位列 - 添加必填标识 -->
        <el-table-column prop="unitOfMeasure" label="单位" min-width="100">
          <template slot="header">
            <span class="required-field">单位</span>
          </template>
          <template slot-scope="scope">
            <div class="input-container">
              <el-input v-if="!readerMode" size="small" v-model="_value[scope.$index].unitOfMeasure" placeholder="请输入"
                :class="{ 'error-border': hasError(scope.$index, 'unitOfMeasure') }" 
                maxlength="6" show-word-limit 
                @blur="validateField(scope.$index, 'unitOfMeasure', _value[scope.$index].unitOfMeasure)" />
              <span v-else>{{ _value[scope.$index].unitOfMeasure || '-' }}</span>
            </div>
            <div v-if="hasError(scope.$index, 'unitOfMeasure')" class="error-message">
              {{ getErrorMessage(scope.$index, 'unitOfMeasure') }}
            </div>
          </template>
        </el-table-column>
        
        <!-- 采购数量列 - 添加必填标识 -->
        <el-table-column prop="quantity" label="采购数量" min-width="110">
          <template slot="header">
            <span class="required-field">采购数量</span>
          </template>
          <template slot-scope="scope">
            <div class="input-container">
              <el-input v-if="!readerMode" size="small" v-model="_value[scope.$index].quantity"
                :class="{ 'error-border': hasError(scope.$index, 'quantity') }" 
                @input="handleQuantityInput(scope.$index, $event)"
                @blur="validateField(scope.$index, 'quantity', _value[scope.$index].quantity)"
                maxlength="16" />
              <span v-else>{{ _value[scope.$index].quantity || 0 }}</span>
            </div>
            <div v-if="hasError(scope.$index, 'quantity')" class="error-message">
              {{ getErrorMessage(scope.$index, 'quantity') }}
            </div>
          </template>
        </el-table-column>
        
        <!-- 估价列 - 添加必填标识 -->
        <el-table-column prop="estimatedUnitPrice" label="估价" min-width="120">
          <template slot="header">
            <span class="required-field">估价</span>
          </template>
          <template slot-scope="scope">
            <div class="input-container">
              <el-input v-if="!readerMode" size="small" v-model="_value[scope.$index].estimatedUnitPrice"
                :class="{ 'error-border': hasError(scope.$index, 'estimatedUnitPrice') }" 
                @input="handlePriceInput(scope.$index, $event)"
                @blur="validateField(scope.$index, 'estimatedUnitPrice', _value[scope.$index].estimatedUnitPrice)"
                maxlength="16" />
              <span v-else>{{ formatMoney(_value[scope.$index].estimatedUnitPrice) }}</span>
            </div>
            <div v-if="hasError(scope.$index, 'estimatedUnitPrice')" class="error-message">
              {{ getErrorMessage(scope.$index, 'estimatedUnitPrice') }}
            </div>
          </template>
        </el-table-column>
        
        <el-table-column class="last-column" prop="requiredDeliveryTime" label="需求到货时间" min-width="230">
          <template slot-scope="scope">
            <div class="input-container">
              <el-date-picker v-if="!readerMode" v-model="_value[scope.$index].requiredDeliveryTime" type="date"
                size="small" value-format="yyyy-MM-dd" placeholder="选择日期" class="custom-date-picker" />
              <span v-else>{{ _value[scope.$index].requiredDeliveryTime || '-' }}</span>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column prop="imageSpecialNotes" label="图片特殊备注" min-width="160">
          <template slot-scope="scope">
            <div class="input-container">
              <el-input v-if="!readerMode" size="small" v-model="_value[scope.$index].imageSpecialNotes"
                placeholder="备注" maxlength="64" show-word-limit />
              <span v-else>{{ _value[scope.$index].imageSpecialNotes || '-' }}</span>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column v-if="!readerMode" fixed="right" min-width="120" label="操作">
          <template slot-scope="scope">
            <div style="display: flex; justify-content: center; gap: 8px;">
              <el-button size="mini" type="text" @click="copyData(scope.$index, scope.row)">复制</el-button>
              <el-button size="mini" type="text" @click="delRow(scope.$index, scope.row)">删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <el-button v-if="!readerMode" type="text" size="small" icon="el-icon-plus" @click="addRow">添加物资</el-button>
    </div>
  </div>
</template>

<script>
import componentMinxins from '../ComponentMinxins'

export default {
  name: 'OfficeSuppliesTable',
  mixins: [componentMinxins],
  props: {
    value: {
      type: Array,
      default: () => []
    },
    readerMode: {
      type: Boolean,
      default: false
    },
    rowLayout: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      validationErrors: {}, // 存储校验错误信息 {行索引: {字段: 错误信息}}
      maxRows: 20, // 最大行数
      isFirstAdd: true // 标记是否是初次添加
    }
  },
  created() {
    if (!Array.isArray(this.value)) {
      this._value = []
    }
  },
  computed: {
    isMaxRows() {
      return this._value.length >= this.maxRows
    }
  },
  methods: {
    copyData(i, row) {
      if (this.isMaxRows) {
        this.$message.warning('最多只能添加' + this.maxRows + '条数据')
        return
      }
      this._value.push(this.$deepCopy(row))
      // 复制数据时不立即校验
    },
    delRow(i) {
      this._value.splice(i, 1)
      // 删除对应的错误信息
      this.$delete(this.validationErrors, i)
      // 重新索引错误信息
      this.reindexErrors()
    },
    addRow() {
      if (this.isMaxRows) {
        this.$message.warning('最多只能添加' + this.maxRows + '条数据')
        return
      }
      this._value.push({
        goodsName: '',
        specification: '',
        unitOfMeasure: '',
        quantity: 0,
        estimatedUnitPrice: 0,
        requiredDeliveryTime: '',
        imageSpecialNotes: ''
      })
      this.$set(this, '_value', this._value)
      
      // 标记不再是初次添加
      if (this.isFirstAdd) {
        this.isFirstAdd = false
      }
    },
    formatMoney(v) {
      const n = Number(v || 0)
      return n.toFixed(2)
    },
    // 处理采购数量输入
    handleQuantityInput(index, value) {
      // 只允许输入数字
      let newValue = value.replace(/[^\d]/g, '')
      
      // 限制输入长度1-16位
      if (newValue.length > 16) {
        newValue = newValue.slice(0, 16)
      }
      
      // 更新值
      this.$set(this._value[index], 'quantity', newValue)
    },
    // 处理估价输入
    handlePriceInput(index, value) {
      // 允许数字和小数点
      let newValue = value.replace(/[^\d.]/g, '')
      
      // 限制只能有一个小数点
      const dotCount = (newValue.match(/\./g) || []).length
      if (dotCount > 1) {
        newValue = newValue.slice(0, -1)
      }
      
      // 限制小数点后最多两位
      if (newValue.includes('.')) {
        const parts = newValue.split('.')
        if (parts[1].length > 2) {
          parts[1] = parts[1].slice(0, 2)
          newValue = parts.join('.')
        }
      }
      
      // 限制总长度1-16位
      if (newValue.length > 16) {
        newValue = newValue.slice(0, 16)
      }
      
      // 更新值
      this.$set(this._value[index], 'estimatedUnitPrice', newValue)
    },
    // 合计方法
    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '合计'
          return
        }

        if (column.property === 'quantity') {
          // 采购数量合计
          const values = data.map(item => Number(item.quantity || 0))
          if (!values.every(value => isNaN(value))) {
            sums[index] = values.reduce((prev, curr) => {
              const value = Number(curr)
              if (!isNaN(value)) {
                return prev + curr
              } else {
                return prev
              }
            }, 0)
          } else {
            sums[index] = 0
          }
        } else if (column.property === 'estimatedUnitPrice') {
          // 估价合计
          const values = data.map(item => Number(item.estimatedUnitPrice || 0))
          if (!values.every(value => isNaN(value))) {
            const total = values.reduce((prev, curr) => {
              const value = Number(curr)
              if (!isNaN(value)) {
                return prev + curr
              } else {
                return prev
              }
            }, 0)
            sums[index] = this.formatMoney(total)
          } else {
            sums[index] = '0.00'
          }
        } else {
          sums[index] = ''
        }
      })

      return sums
    },
    // 校验单个字段
    validateField(rowIndex, field, value) {
      // 如果是初次添加，不进行校验
      if (this.isFirstAdd) {
        return true
      }

      let isValid = true
      let message = ''

      switch (field) {
        case 'goodsName':
          if (!value || value.trim() === '') {
            isValid = false
            message = '物品名称不能为空'
          } else if (value.length > 24) {
            isValid = false
            message = '物品名称不能超过24个字符'
          }
          break
        case 'unitOfMeasure':
          if (!value || value.trim() === '') {
            isValid = false
            message = '单位不能为空'
          } else if (value.length > 6) {
            isValid = false
            message = '单位不能超过6个字符'
          }
          break
        case 'quantity':
          const quantity = Number(value)
          if (isNaN(quantity) || quantity <= 0) {
            isValid = false
            message = '采购数量必须大于0'
          } else if (value.length < 1 || value.length > 16) {
            isValid = false
            message = '采购数量长度应为1-16位'
          } else if (!/^\d+$/.test(value)) {
            isValid = false
            message = '采购数量必须为整数'
          }
          break
        case 'estimatedUnitPrice':
          const price = Number(value)
          if (!value || value.toString().trim() === '') {
            isValid = false
            message = '估价不能为空'
          } else if (isNaN(price) || price < 0) {
            isValid = false
            message = '估价不能为负数'
          } else if (value.length < 1 || value.length > 16) {
            isValid = false
            message = '估价长度应为1-16位'
          } else if (!/^\d+(\.\d{1,2})?$/.test(value)) {
            isValid = false
            message = '估价必须为数字，最多两位小数'
          }
          break
        case 'specification':
          if (value && value.length > 24) {
            isValid = false
            message = '规格不能超过24个字符'
          }
          break
        case 'imageSpecialNotes':
          if (value && value.length > 64) {
            isValid = false
            message = '图片特殊备注不能超过64个字符'
          }
          break
      }

      // 更新错误信息
      if (!isValid) {
        if (!this.validationErrors[rowIndex]) {
          this.$set(this.validationErrors, rowIndex, {})
        }
        this.$set(this.validationErrors[rowIndex], field, message)
      } else {
        if (this.validationErrors[rowIndex] && this.validationErrors[rowIndex][field]) {
          this.$delete(this.validationErrors[rowIndex], field)
          if (Object.keys(this.validationErrors[rowIndex]).length === 0) {
            this.$delete(this.validationErrors, rowIndex)
          }
        }
      }

      return isValid
    },
    // 校验所有行
    validateAll() {
      this.validationErrors = {}
      let allValid = true

      // 强制校验所有行，忽略初次添加标记
      const wasFirstAdd = this.isFirstAdd
      this.isFirstAdd = false

      this._value.forEach((row, index) => {
        const validations = [
          this.validateField(index, 'goodsName', row.goodsName),
          this.validateField(index, 'unitOfMeasure', row.unitOfMeasure),
          this.validateField(index, 'quantity', row.quantity),
          this.validateField(index, 'estimatedUnitPrice', row.estimatedUnitPrice),
          this.validateField(index, 'specification', row.specification),
          this.validateField(index, 'imageSpecialNotes', row.imageSpecialNotes)
        ]

        if (validations.some(valid => !valid)) {
          allValid = false
        }
      })

      // 恢复标记状态
      this.isFirstAdd = wasFirstAdd

      return allValid
    },
    // 检查是否有错误
    hasError(rowIndex, field) {
      return this.validationErrors[rowIndex] && this.validationErrors[rowIndex][field]
    },
    // 获取错误信息
    getErrorMessage(rowIndex, field) {
      return this.validationErrors[rowIndex] && this.validationErrors[rowIndex][field] || ''
    },
    // 重新索引错误信息（当删除行时）
    reindexErrors() {
      const newErrors = {}
      Object.keys(this.validationErrors).forEach(oldIndex => {
        const newIndex = oldIndex > this._value.length ? null : oldIndex
        if (newIndex !== null) {
          newErrors[newIndex] = this.validationErrors[oldIndex]
        }
      })
      this.validationErrors = newErrors
    },
    // 暴露给父组件的校验方法
    validate(callback) {
      const isValid = this.validateAll()
      callback(isValid)
    }
  }
}
</script>

<style scoped lang="scss">
.office-supplies-table {
  /deep/ .el-table {
    // 必填字段表头样式
    .required-field::before {
      content: "*";
      color: #f56c6c;
      margin-right: 4px;
    }

    .error-border {
      .el-input__inner {
        border-color: #f56c6c;
      }
    }

    // 输入框容器，固定高度
    .input-container {
      min-height: 32px;
      display: flex;
      align-items: center;
    }

    .el-table__body {
      .error-message {
        color: #f56c6c;
        font-size: 12px;
        line-height: 1;
        margin-top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
    }

    .custom-date-picker {
      .el-input__inner {
        padding-left: 30px;
      }
    }

    /* 限制输入框字数统计样式 */
    .el-input {
      .el-input__suffix {
        .el-input__count {
          font-size: 12px;
          color: #909399;
        }
      }
    }

    // 固定行高，避免错误信息影响布局
    .el-table__row {
      .el-table__cell {
        padding: 4px 0;
        
        .cell {
          min-height: 40px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
      }
    }
  }
}
</style>